import React, { useState } from 'react';

import { Formik } from 'formik';

import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Button from 'src/components/ui/Button';
import EmptyState from 'src/components/ui/EmptyState';
import Select from 'src/components/ui/Select';
import Loader from 'src/components/ui/Loader';
import { PropertyTypesSelect } from 'src/components/ad/PropertyTypesSelect';
import TinyPreviewCard from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';
import BudgetButton from 'src/components/news/BudgetButton';
import GooglePlacesAutocomplete from 'src/components/ui/GooglePlacesAutocomplete';

import { colours } from 'src/constants/colours';
import { maxLengthRegex } from 'src/constants/regexes';
import useGetRoomsNumberItems from 'src/hooks/useGetRoomsNumberItems';

import type { Ad, Medium } from 'src/types/__generated__/graphql';
import { useCompanyAdsSearchQuery, useUserAdsSearchQuery } from 'src/hooks/__generated__/queries';
import type { ExtendedAdsQueryVariables } from 'src/components/ad/SearchAdsPage';
import formatPrice from 'src/utils/formatPrice';
import { isBottomDiv } from 'src/utils/isBottom';
import { styledComponent, styledTag } from 'src/utils/styled';

const StyledCard = styledComponent(Card)<{
    $selected: boolean;
}>`
    ${({ $selected }) =>
        $selected &&
        `
        background-color: ${colours.secondary.O12};
    `}

    :not(:first-of-type) {
        margin-top: 0.7rem;
    }
    :last-of-type {
        margin-bottom: 0.7rem;
    }
    box-shadow: 0rem 0.25rem 2rem -0.5rem ${colours.blackOpacity.O16};
    position: relative;
`;

const StyledDialog = styledComponent(Dialog)`
    .MuiDialog-paper {
        position: relative;
        height: 48.5rem;
        width: 32rem;
    }
`;

const StyledDialogContent = styledComponent(DialogContent)`
    display: flex;
    flex-direction: column;
    padding: 0 1rem;
`;

const StyledDialogActions = styledComponent(DialogActions)`
    border-top: 0.0625rem solid ${colours.grey.G200};
    padding: 1rem;
    margin-top: 1rem;
`;

const ScrollContainer = styledTag('div')`
    overflow-y: auto;
`;

const Form = styledComponent(Box)`
    display: flex;
    .MuiInputBase-root {
        height: 2.5rem;
    }
    .MuiSelect-select:focus {
        background-color: transparent;
    }
    .MuiFormControl-root {
        margin: 0.5rem auto;
    }
`;

StyledDialog.defaultProps = {
    maxWidth: 'sm',
    fullWidth: true,
};

const StyledSelect = styledComponent(Select)`
    margin: 0rem;
`;

type FormValues = Omit<ExtendedAdsQueryVariables, 'minPrice' | 'maxPrice' | 'rooms' | 'orderBy'> & {
    minPrice: number | undefined;
    maxPrice: number | undefined;
    rooms: string;
    orderBy: string;
};

interface Props {
    onSelection: (selectedAdId: string) => void;
    open: boolean;
    onClose: () => void;
    userId?: string;
    companyId?: string;
}

const SelectAdModal: React.FC<Props> = ({ onSelection, open, onClose, userId, companyId }) => {
    const { t, i18n } = useTranslation(['news', 'ad', 'community', 'common']);
    const [selectedAdId, setSelectedAdId] = useState<string | undefined>();

    const defaultQueryVariables = {
        offset: 0,
        limit: 12,
        orderBy: { createdAt: 'DESC' } as ExtendedAdsQueryVariables['orderBy'],
        minPrice: undefined,
        maxPrice: undefined,
        rooms: undefined,
        house: false,
        apartment: false,
        loft: false,
        parking: false,
        terrain: false,
        newProperty: false,
        oldProperty: false,
        type: 'sell',
    };

    const transformQueryVariablesInFormValues = (variables?: ExtendedAdsQueryVariables): FormValues => ({
        ...variables,
        rooms: variables?.rooms ? String(variables.rooms) : 'rooms',
        maxPrice: variables?.maxPrice ? Number(variables.maxPrice) : undefined,
        minPrice: variables?.minPrice ? Number(variables.minPrice) : undefined,
        orderBy: JSON.stringify(variables?.orderBy),
    });

    const [queryVariables, setQueryVariables] = useState<ExtendedAdsQueryVariables>(defaultQueryVariables);

    const updateSearch = (searchParams: ExtendedAdsQueryVariables) => {
        setSelectedAdId(undefined);
        setQueryVariables(searchParams);
    };

    const {
        previousData: userAdsPreviousData,
        data: userAds = userAdsPreviousData,
        loading: loadingUserAds,
        fetchMore: fetchMoreUserAds,
    } = useUserAdsSearchQuery({
        variables: {
            userId: String(userId),
            ...queryVariables,
        },
        fetchPolicy: 'cache-and-network',
        skip: !open || !userId,
    });

    const {
        previousData: companyAdsPreviousData,
        data: companyAds = companyAdsPreviousData,
        loading: loadingCompanyAds,
        fetchMore: fetchMoreCompanyAds,
    } = useCompanyAdsSearchQuery({
        variables: {
            companyId: String(companyId),
            ...queryVariables,
        },
        fetchPolicy: 'cache-and-network',
        skip: !open || !companyId,
    });

    const handleClose = () => {
        setSelectedAdId(undefined);
        setQueryVariables(defaultQueryVariables);
        onClose();
    };
    const adsTotalCount = userAds?.ads?.totalCount ?? companyAds?.ads?.totalCount ?? 0;

    const handleScroll = async (ev: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const bottom = isBottomDiv(ev.currentTarget);
        const adsActualCount = userAds?.ads?.items?.length ?? companyAds?.ads?.items?.length ?? 0;
        const allDataLoaded = adsActualCount >= adsTotalCount;
        if (bottom && adsActualCount && !allDataLoaded) {
            if (userId) {
                await fetchMoreUserAds({
                    variables: {
                        offset: adsActualCount,
                    },
                });
            } else if (companyId) {
                await fetchMoreCompanyAds({
                    variables: {
                        offset: adsActualCount,
                    },
                });
            }
        }
    };

    const adsToDisplay = ((userId ? userAds?.ads?.items : companyAds?.ads?.items) ?? []).map((ad) => (
        <StyledCard
            key={ad.id}
            data-target="ad-modal-card"
            data-target-id={`ad-modal-card-${ad.id}`}
            elevation={0}
            onClick={() => setSelectedAdId(ad.id)}
            $selected={ad.id === selectedAdId}
        >
            <TinyPreviewCard
                iconName="AdIcon"
                ad={ad as Ad}
                type="ad"
                media={ad.media as Medium[]}
                subtitle={formatPrice({
                    price: Number(ad.price ?? ad.rent),
                    lang: i18n.language,
                })}
                title={t(`ad:propertyTypes.${ad.propertyType}`)}
            />
        </StyledCard>
    ));
    return (
        <StyledDialog open={open} onClose={handleClose} fullWidth>
            <DialogTitle onClose={handleClose}>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <Typography variant="h3">{t('news:linkAd')}</Typography>
                </Box>
            </DialogTitle>

            <Formik initialValues={transformQueryVariablesInFormValues(queryVariables)} onSubmit={console.info}>
                <Form display="flex" flexDirection="column" flex="0" margin="0rem" mb={1}>
                    <Box display="flex" flexDirection="row" alignItems="center">
                        <Box ml={2} flex="1">
                            <GooglePlacesAutocomplete
                                name="place"
                                label={t('common:location')}
                                onChangeOption={(option) => {
                                    updateSearch({
                                        ...queryVariables,
                                        place: option
                                            ? {
                                                  googlePlaceId: option.googlePlaceId,
                                                  description: option.description ?? '',
                                                  cityName: option.cityName,
                                              }
                                            : undefined,
                                        places: option?.geometry ? [option.geometry] : undefined,
                                    });
                                }}
                                heightVariant="small"
                                includeGeometry
                                onlyFrance
                            />
                        </Box>
                        <Box ml={2} mr={2} width="7.5rem" height="fit-content">
                            <StyledSelect
                                name="type"
                                items={[
                                    {
                                        label: t('ad:buy'),
                                        value: 'sell',
                                    },
                                    {
                                        label: t('ad:rent'),
                                        value: 'rent',
                                    },
                                ]}
                                onChange={(value) => {
                                    updateSearch({
                                        ...queryVariables,
                                        type: value as ExtendedAdsQueryVariables['type'],
                                    });
                                }}
                                InputProps={{
                                    inputProps: {
                                        'data-target': 'ad-type',
                                        'data-target-id': 'ad-type',
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                    <Box display="flex" flexDirection="row" alignItems="center">
                        <Box ml={2} width="10.5rem">
                            <PropertyTypesSelect
                                name="PropertyTypeSelect"
                                onChange={(newValues) =>
                                    updateSearch({
                                        ...queryVariables,
                                        ...newValues,
                                    })
                                }
                                groupTypesToDisplay={
                                    queryVariables.type === 'sell' ? undefined : ['advertiser', 'housing']
                                }
                            />
                        </Box>
                        <Box ml={2} width="6rem">
                            <StyledSelect
                                name="rooms"
                                items={[
                                    {
                                        label: t('ad:rooms'),
                                        value: 'rooms',
                                    },
                                    ...useGetRoomsNumberItems('rooms'),
                                ]}
                                onChange={(value) => {
                                    updateSearch({
                                        ...queryVariables,
                                        rooms: value !== 'rooms' ? Number(value) : undefined,
                                    });
                                }}
                            />
                        </Box>
                        <Box ml={2} mr={2} flex="1">
                            <BudgetButton
                                onChangeMin={(value) => {
                                    if (maxLengthRegex(9).test(value)) {
                                        updateSearch({
                                            ...queryVariables,
                                            minPrice: value === 'default' ? undefined : Number(value),
                                        });
                                    }
                                }}
                                onChangeMax={(value) => {
                                    if (maxLengthRegex(9).test(value)) {
                                        updateSearch({
                                            ...queryVariables,
                                            maxPrice: value === 'default' ? undefined : Number(value),
                                        });
                                    }
                                }}
                                queryVariables={queryVariables}
                            />
                        </Box>
                    </Box>
                </Form>
            </Formik>

            <StyledDialogContent>
                <ScrollContainer onScroll={handleScroll}>
                    {adsToDisplay}
                    {(loadingUserAds || loadingCompanyAds) && (
                        <Box height="3rem" width="100%">
                            <Loader />
                        </Box>
                    )}
                </ScrollContainer>
                {adsTotalCount === 0 && !(loadingUserAds || loadingCompanyAds) ? (
                    <Box mt="1rem">
                        <EmptyState icon="SearchPerson" text1={t('common:noSearchResult')} />
                    </Box>
                ) : null}
            </StyledDialogContent>

            <StyledDialogActions>
                <Box display="flex" flexDirection="row" justifyContent="end">
                    <Button
                        data-target="ad-modal-button"
                        data-target-id="ad-modal-cancel"
                        variant="outlined"
                        onClick={handleClose}
                    >
                        {t('common:cancel')}
                    </Button>
                    <Box ml={0.5}>
                        <Button
                            data-target="ad-modal-button"
                            data-target-id="ad-modal-link"
                            onClick={() => {
                                if (selectedAdId) {
                                    onSelection(selectedAdId);
                                }
                                setQueryVariables(defaultQueryVariables);
                                handleClose();
                            }}
                            disabled={!selectedAdId}
                        >
                            {t('news:link')}
                        </Button>
                    </Box>
                </Box>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default SelectAdModal;
